/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { CSSProperties, useMemo } from "react";
import { css, cx } from "@panda/css";
import { ColorToken, token } from "@panda/tokens";
import { CosmosTextAs, CosmosTextVariantEnum, CosmosVariableRichText } from "../text";
import { useFormatHtml } from "./CosmosRichText.utils";
import { getTextAs, getTextVariantType } from "../text/text.utils";

export interface CosmosRichTextProps extends Omit<CosmosVariableRichText, "text" | "as"> {
	children?: React.ReactNode;
	className?: string;
	isGameMode?: boolean;
	color?: ColorToken;
	style?: CSSProperties;
	as?: CosmosTextAs | null;
}

interface CssVariables extends CSSProperties {
	"--color"?: string;
}

const styles = css({
	color: "var(--color)",
	"& a[href]": {
		color: "eds.fill.primary.default",
		textDecoration: "underline",
		transition: `color token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)`,
		_hover: {
			color: "eds.fill.primary.hover",
		},
	},
	"& ol, & ul": {
		listStyleType: "revert",
		marginBlock: "eds.dimension.16",
		paddingInlineStart: "eds.dimension.40",
		"& li": {
			marginBlockEnd: "eds.dimension.12",
			"&:last-child": {
				marginBlockEnd: "0",
			},
		},
	},
	"& p": {
		marginBlock: "eds.dimension.16",
	},
	"& p:first-child": {
		marginBlockStart: "0",
	},
	"& p:last-child": {
		marginBlockEnd: "0",
	},
	"& blockquote": {
		borderInlineStartWidth: "4px",
		borderInlineStartStyle: "solid",
		borderInlineStartColor: "eds.fill.primary.default",
		paddingInlineStart: "eds.dimension.32",
		paddingBlock: "eds.dimension.8",
	},
	"& i, & em": {
		fontStyle: "italic",
	},
	"& code": {
		fontFamily: "revert",
	},
	"& hr": {
		marginBlock: "eds.dimension.32",
		width: "100%",
		borderBlockStartWidth: "2px",
		borderBlockStartStyle: "solid",
		borderBlockStartColor: "eds.border.subtle",
	},
});

export const CosmosRichText = (props: CosmosRichTextProps): React.JSX.Element | null => {
	const __html = useFormatHtml(props.html, { gameMode: props.isGameMode });

	const style: CssVariables = useMemo(() => {
		if (props.color) {
			return { ...props.style, "--color": token(`colors.${props.color}`) };
		}
		return props.style || {};
	}, [props.color, props.style]);

	if (!props.html) {
		return null;
	}

	const as = getTextAs(props);
	const Component = as && !props.html.startsWith("<h1>") ? as : "span";

	const variantType = getTextVariantType(props.variant);

	return (
		<Component
			className={cx(
				"cosmos-text",
				"cosmos-rich-text",
				variantType,
				css({
					textStyle: props.variant || CosmosTextVariantEnum.paragraphMD,
					overflowWrap: "anywhere",
				}),
				styles,
				props.className || "",
			)}
			style={style}
			dangerouslySetInnerHTML={{ __html }}
		></Component>
	);
};
